import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { I18nString, PermitCategory } from '../../../models/permit-category.model';
import { FormFieldConfig, PermitTypeConfig, displayUnitFor, permitTypeSubtitle } from '../../../models/permit-type.model';
import { LocationInfo } from '../../../models/permit.model';

@Component({
    selector: 'app-permit-review-step',
    templateUrl: './permit-review-step.component.html',
    styleUrls: ['./permit-review-step.component.scss'],
})
export class PermitReviewStepComponent {
    @Input() citizen: any | null = null;

    @Input() selectedCategory: PermitCategory | null = null;

    @Input() selectedType: PermitTypeConfig | null = null;

    @Input() location: LocationInfo | null = null;

    @Input() formData: Record<string, any> = {};

    /** originalName per fieldId for `file` fields — formData only carries the blob URL. */
    @Input() fileNames: Record<string, string> = {};

    constructor(private translate: TranslateService) {}

    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    typeSubtitle(type: PermitTypeConfig | undefined | null): string {
        return permitTypeSubtitle(type, this.translate.currentLang);
    }

    citizenName(): string {
        if (!this.citizen) return '';
        const composed = `${this.citizen.firstName || ''} ${this.citizen.lastName || ''}`.trim();
        return composed || this.citizen.name || '';
    }

    // ─── Summary of the dynamic form ─────────────────────────────────────────

    /** Field labels live on the type config, so the review shows real labels, not raw field ids. */
    get formDataEntries(): { label: string; value: string }[] {
        const fields = this.selectedType?.formFields || [];
        return Object.keys(this.formData || {})
            .map((fieldId) => {
                const field = fields.find((f) => f.fieldId === fieldId);
                return { label: field ? this.fieldLabel(field) : fieldId, value: this.displayValue(field, this.formData[fieldId], fieldId) };
            })
            .filter((entry) => entry.value !== '');
    }

    private fieldLabel(field: FormFieldConfig): string {
        const { label } = field;
        if (!label) return field.fieldId;
        const lang = this.translate.currentLang || 'fr';
        return label[lang] || label.fr || label.en || field.fieldId;
    }

    private displayValue(field: FormFieldConfig | undefined, value: any, fieldId: string): string {
        if (value === null || value === undefined || value === '') return '';
        if (typeof value === 'boolean') {
            return this.translate.instant(value ? 'permit.create.review.yes' : 'permit.create.review.no');
        }
        if (field?.fieldType === 'date') {
            const date = new Date(value);
            return isNaN(date.getTime()) ? `${value}` : date.toLocaleDateString(this.translate.currentLang === 'en' ? 'en-CA' : 'fr-CA');
        }
        if (field?.fieldType === 'file') {
            return this.fileNames[fieldId] || `${value}`.split('?')[0].split('/').pop() || `${value}`;
        }
        if (field?.fieldType === 'number') {
            const lang = this.translate.currentLang || 'fr';
            const unit = displayUnitFor(field, this.selectedType?.fieldUnits, lang);
            return unit ? `${value} ${unit}` : `${value}`;
        }
        return `${value}`;
    }

    // ─── Fee summary ─────────────────────────────────────────────────────────

    get hasFee(): boolean {
        return !!this.selectedType?.feeConfig;
    }

    get feeAmount(): number | null {
        const fee = this.selectedType?.feeConfig;
        return fee?.amount != null ? fee.amount : null;
    }

    get feeTimingKey(): string {
        const timing = this.selectedType?.feeConfig?.timing || 'upfront';
        return `permit.manage.info.fee_timing_${timing}`;
    }

    get feeDescription(): string {
        const description = this.selectedType?.feeConfig?.description as any;
        if (!description) return '';
        return typeof description === 'string' ? description : this.localizedName(description);
    }

    // ─── Location summary ────────────────────────────────────────────────────

    get locationText(): string {
        if (!this.location) return '';
        if (this.location.address) return this.location.address;
        if (this.location.coordinates) return `${this.location.coordinates.lat}, ${this.location.coordinates.lng}`;
        return '';
    }
}
